import { localStorage } from '#utils/localStorage';
import type { NativePushState } from './nativePushState';

/**
 * Почему мы заговорили с человеком о уведомлениях.
 *
 * Причина — не «мы давно не спрашивали» и не «сменилась сборка», а конкретное состояние,
 * из-за которого пуши до телефона не доедут:
 *
 * - `denied` — уведомления выключены в системе; диалога больше не будет, только настройки;
 * - `prompt` — разрешение ещё ни разу не спрашивали;
 * - `no-token` — разрешение есть, а токена этого устройства на сервере нет. Самый тихий и
 *   самый частый случай: человек закрыл шторку словом «Позже», разрешение осталось нетронутым
 *   (а на Android 12 его и нет вовсе — там всегда «granted»), и приложение выглядит здоровым,
 *   пока ему не приходит ни одного пуша.
 */
export type PushAttentionReason = 'denied' | 'prompt' | 'no-token';

/**
 * Сколько молчать после того, как человек закрыл шторку по этой причине.
 *
 * Отказ на системном экране — осознанный выбор, его переспрашивают редко. «Нет токена» чаще
 * означает случайно закрытую шторку, чем решение, поэтому срок короче: неделя.
 */
export const PUSH_ATTENTION_BACKOFF_MS: Record<PushAttentionReason, number> = {
  denied: 30 * 24 * 60 * 60 * 1_000,
  prompt: 7 * 24 * 60 * 60 * 1_000,
  'no-token': 7 * 24 * 60 * 60 * 1_000,
};

/**
 * Ключ отметки «спрашивали по этому поводу».
 *
 * Отметка привязана к ПРИЧИНЕ, а не к версии сборки и не к аккаунту. Версия была неверным
 * основанием: измерено, что MIUI снимает при обновлении только автозапуск, который на доставку
 * пушей не влияет вовсе, — а разрешение на уведомления и токен обновление переживают. Аккаунт
 * тоже не подходит: разрешение выдаётся телефону, а не человеку.
 */
export const pushAttentionAskedKey = (reason: PushAttentionReason) =>
  `push-attention-asked:${reason}`;

/**
 * Что из состояния устройства требует разговора. `ok` и `unknown` — не требуют: первое потому,
 * что всё работает, второе потому, что мы ещё не знаем, и пугать раньше времени нечестно.
 */
export const reasonFromState = (state: NativePushState): PushAttentionReason | null => {
  switch (state) {
    case 'denied':
      return 'denied';
    case 'prompt':
      return 'prompt';
    case 'no-token-on-server':
      return 'no-token';
    default:
      return null;
  }
};

/** Когда в последний раз спрашивали по этой причине. `null` — не спрашивали никогда. */
export const readPushAttentionAskedAt = (reason: PushAttentionReason): number | null => {
  const raw = localStorage.getItem(pushAttentionAskedKey(reason));
  if (!raw) {
    return null;
  }
  const at = Number.parseInt(raw, 10);
  return Number.isFinite(at) ? at : null;
};

/** Запомнить, что спросили. Пишется при закрытии шторки, а не при показе. */
export const markPushAttentionAsked = (reason: PushAttentionReason, now: number) =>
  localStorage.setItem(pushAttentionAskedKey(reason), String(now));

/**
 * Показывать ли шторку и по какому поводу.
 *
 * Чистая функция: это единственное место, где решается, занимать ли человеку экран, и ошибка
 * здесь стоит либо назойливости, либо молчаливо неработающих уведомлений.
 *
 * Отметка из будущего (уехавшие часы) и битая отметка читаются как «не спрашивали»: цена
 * ошибки в эту сторону — одна лишняя шторка, в обратную — человек, который никогда не узнает,
 * что до него не доходят сообщения.
 */
export const resolvePushAttention = ({
  state,
  askedAt,
  now,
}: {
  state: NativePushState;
  askedAt: number | null;
  now: number;
}): PushAttentionReason | null => {
  const reason = reasonFromState(state);
  if (!reason) {
    return null;
  }
  if (askedAt === null || askedAt > now) {
    return reason;
  }
  return now - askedAt >= PUSH_ATTENTION_BACKOFF_MS[reason] ? reason : null;
};
